import { type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { UserMenu } from "@/components/auth/UserMenu";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { Chevron } from "@/components/ui/Chevron";
import { cn } from "@/lib/utils";

interface AppShellProps {
  children: ReactNode;
}

/**
 * Application shell: header (brand + nav + user menu) + main + footer.
 * Header uses the terminal-prompt chevron as the brand mark — a
 * deliberate OpenClaw cue. v0.1 omits a sidebar (single primary flow).
 */
export function AppShell({ children }: AppShellProps) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60">
        <div className="container flex h-16 items-center justify-between gap-4">
          <NavLink
            to="/"
            className="group flex items-center gap-2 text-lg font-semibold tracking-tight"
          >
            <Chevron className="text-base group-hover:text-primary transition-colors" />
            <span>PTRN</span>
            <span className="text-muted-foreground font-normal">
              Pattern Agent
            </span>
          </NavLink>
          <div className="flex items-center gap-2">
            <nav className="flex items-center gap-1 text-sm text-muted-foreground">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-3 py-1.5 hover:text-foreground hover:bg-secondary/60 transition-colors",
                    isActive && "text-foreground bg-secondary",
                  )
                }
              >
                New scenario
              </NavLink>
              {/* /history lands in Phase 3.8 (deploy audit log) */}
            </nav>
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="container py-10">{children}</main>
      <footer className="border-t border-border/60 mt-16 py-6 text-xs text-muted-foreground">
        <div className="container flex items-center justify-between">
          <span>
            <Chevron className="mr-1" />
            v0.1 · Pattern Agent
          </span>
          <span className="font-mono">ptrn.frontierfirm.app</span>
        </div>
      </footer>
    </div>
  );
}
